import { addDays, addWeeks, format, startOfWeek, subWeeks } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../styles/index.css';

import {
  DeleteFilled,
  EditOutlined,
  FileAddOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { App, Button, Popconfirm, Spin, Tag, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import { useDepartments } from '@/common/useDepartments';
import { CalendarGridSkeleton } from '@/components/skeletons/CalendarGridSkeleton';
import type {
  GetDriverWindowSlotsQuery,
  GetWeekdayWindowForSlotQuery,
  GetWeekdayWindowsAllQuery,
  Weekday_Window,
} from '@/generated/graphql/graphql';
import dayjs from '@/utils/dayjs-instance';
import { formatError, logError } from '@/utils/formatError';
import {
  useDeleteDriverDistrictOld,
  useDeleteWindowDeliveryProvidersByDriverWindow,
  useGetCountryHolidaysByDepartmentId,
  useGetDriverWindowSlots,
  useGetWeekdayWindowsAll,
  useGetWeekdayWindowsForSlot,
  useRemoveDriverWindowSlot,
  useRemoveWeekdayWindowDistricts,
  useRemoveWeekdayWindowDistrictsByWindowId,
  useRemoveWeekdayWindowDistrictsList,
  useRemoveWeekdayWindows,
} from '../../query/calendarGrid.hooks';
import { useGetDistrictsDriverList } from '../../query/districtsTable.hooks';
import { AddDistrictModal } from './AddDistrictModal';
import { DistrictTag } from './DistrictTag';
import { DriverHolidays } from './DriverHolidays';
import { DriverSelect } from './DriverSelect';
import {
  getProviderState,
  getProviderStateColor,
  getProviderStateLabel,
  type ProviderState,
} from './ProviderSelect';
import { SlotModal } from './SlotModal';
import { DistrictDeleteType, useCalendarState } from './useCalendarState';
import { useModalState } from './useModalState';
import { WeekdayProviderModal } from './WeekdayProviderModal';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

export const isDistrictPresent = (data: GetWeekdayWindowsAllQuery, districtId: string): boolean => {
  return data.weekday_window.some((window) =>
    window.weekday_window_districts.some((district) => district?.district?.id === districtId)
  );
};

type WeekdayWindowWithProviders =
  GetDriverWindowSlotsQuery['driver_window'][number]['weekday_windows'][number];

const getWeekdayWindowProviders = (
  weekdayWindow: WeekdayWindowWithProviders | undefined | null
): { providerIds: string[]; providerNames: string[]; state: ProviderState } => {
  if (!weekdayWindow) {
    return { providerIds: [], providerNames: [], state: 'dtk_only' };
  }

  const providers = weekdayWindow.window_delivery_providers;

  if (!providers || providers.length === 0) {
    return { providerIds: [], providerNames: ['DTK'], state: 'dtk_only' };
  }

  const providerIds = providers.map((p) => p.provider_id);
  const providerNames = providers.map(
    (p) => p.delivery_provider?.display_name ?? p.delivery_provider?.short_name ?? 'Unknown'
  );
  const shortNames = providers
    .map((p) => p.delivery_provider?.short_name)
    .filter((name): name is string => Boolean(name));
  const state = getProviderState(shortNames);

  return { providerIds, providerNames, state };
};

export const CalendarGrid = () => {
  const { message } = App.useApp();
  const { selectedDepartmentId: departmentId } = useDepartments();

  const [holidays, setHolidays] = useState<Record<string, 'holiday' | 'forced'>>({});

  const handleHolidaysChange = useCallback((holidays: Record<string, 'holiday' | 'forced'>) => {
    setHolidays(holidays);
  }, []);

  const [modalTitle, setModalTitle] = useState<string | null>(null);
  const {
    isVisible: isSlotModalVisible,
    openModal: openSlotModal,
    closeModal: closeSlotModal,
  } = useModalState();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const {
    isVisible: isDistrictModalVisible,
    openModal: openDistrictModal,
    closeModal: closeDistrictModal,
  } = useModalState();

  const {
    isVisible: isWeekdayProviderModalVisible,
    openModal: openWeekdayProviderModal,
    closeModal: closeWeekdayProviderModal,
  } = useModalState();
  const [weekdayProviderModalData, setWeekdayProviderModalData] = useState<{
    weekdayWindowId: number;
    weekdayName: string;
    timeDuration: string;
    currentProviderIds: string[];
  } | null>(null);

  const handleEditWeekdayProviders = (
    weekdayWindowId: number,
    weekdayName: string,
    timeDuration: string,
    currentProviderIds: string[]
  ) => {
    setWeekdayProviderModalData({
      weekdayWindowId,
      weekdayName,
      timeDuration,
      currentProviderIds,
    });
    openWeekdayProviderModal();
  };

  const handleCloseWeekdayProviderModal = () => {
    setWeekdayProviderModalData(null);
    closeWeekdayProviderModal();
  };

  const { data: disabledWeekdaysForDistricts } = useGetDistrictsDriverList({ departmentId });

  const { data: countryHolidays } = useGetCountryHolidaysByDepartmentId({ departmentId });

  const combinedHolidayStyles = React.useMemo(() => {
    const combinedStyles: Record<string, 'holiday' | 'forced' | 'countryHoliday'> = {};

    if (countryHolidays?.country_holiday) {
      countryHolidays.country_holiday.forEach((holiday) => {
        const date = dayjs.utc(holiday.date).format('YYYY-MM-DD');
        combinedStyles[date] = 'countryHoliday';
      });
    }

    Object.entries(holidays).forEach(([date, type]) => {
      combinedStyles[date] = type === 'forced' ? 'forced' : combinedStyles[date] || 'holiday';
    });

    return combinedStyles;
  }, [holidays, countryHolidays]);

  const isDistrictDisabledForDay = (
    districtId: string,
    dayIndex: number,
    disabledDistrictsData: Record<string, unknown>
  ): boolean => {
    const districts = disabledDistrictsData?.district as Array<Record<string, unknown>> | undefined;
    const district = districts?.find((d: Record<string, unknown>) => d.id === districtId);
    return district ? (district.offDays as number[]).includes(dayIndex + 1) : false;
  };

  const getDistrictTooltipMessage = (
    districtId: string,
    disabledDistrictsData: Record<string, unknown>
  ): string => {
    const districts = disabledDistrictsData?.district as Array<Record<string, unknown>> | undefined;
    const district = districts?.find((d: Record<string, unknown>) => d.id === districtId);
    return district ? `${district.name as string} is disabled due to off-days` : '';
  };

  const {
    state,
    setLoadingDistrictId,
    setIsEditMode,
    setSelectedTimeRange,
    setSelectedSlotId,
    setSelectedDistricts,
    setSelectedDayIndex,
    setDistrictDeleteType,
    setTimeSlots,
  } = useCalendarState();

  const { data: driverWindowSlotsData, isLoading: driverSlotsLoading } = useGetDriverWindowSlots({
    driverId: state.selectedDriverId ?? '',
  });

  const { refetch: weekdayWindowsForSlotRefetch } = useGetWeekdayWindowsForSlot({
    driverWindowId: state.selectedSlotId ?? 0,
  });

  const { refetch: weekDayWindowsAllRefetch } = useGetWeekdayWindowsAll({
    driverId: state.selectedDriverId ?? '',
  });

  const { mutateAsync: removeWindowDeliveryProvidersByDriverWindow } =
    useDeleteWindowDeliveryProvidersByDriverWindow();

  const { mutateAsync: removeWeekdayWindows } = useRemoveWeekdayWindows();

  const { mutateAsync: removeWeekdayWindowDistrictsByWindowId } =
    useRemoveWeekdayWindowDistrictsByWindowId();

  const { mutateAsync: removeDriverWindowSlot } = useRemoveDriverWindowSlot();

  const { mutateAsync: removeWeekdayWindowDistricts } = useRemoveWeekdayWindowDistricts();
  const { mutateAsync: removeWeekdayWindowDistrictsList } = useRemoveWeekdayWindowDistrictsList();

  const handleAddClick = (
    slotId: number,
    dayIndex: number,
    selectedDistricts: string[],
    date: Dayjs,
    timeDuration: string
  ) => {
    const formattedDate = date.format('YYYY-MM-DD');
    const title = `Slot ${timeDuration} on ${formattedDate}`;
    setModalTitle(title);
    setSelectedSlotId(slotId);
    setSelectedDayIndex(dayIndex);
    setSelectedDistricts(selectedDistricts);
    openDistrictModal();
  };

  const handleEditTimeSlot = (slotId: number, timeDuration: string) => {
    const [start, end] = timeDuration.split('-');
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    const base = dayjs().startOf('day');
    setSelectedTimeRange([
      base.hour(startHour).minute(startMinute),
      base.hour(endHour).minute(endMinute),
    ]);
    setSelectedSlotId(slotId);
    setIsEditMode(true);
    openSlotModal();
  };

  const handleAddTimeSlotClick = () => {
    openSlotModal();
  };

  const handleCancel = () => {
    closeSlotModal();
    closeDistrictModal();
    setSelectedDistricts(null);
    setIsEditMode(false);
  };

  const handleRemoveSlot = async (driverWindowId: number) => {
    setSelectedSlotId(driverWindowId);
    try {
      await removeWindowDeliveryProvidersByDriverWindow({
        driverWindowId,
      });

      await removeWeekdayWindowDistrictsByWindowId({
        driverWindowId,
      });

      await removeWeekdayWindows({
        driverWindowId,
      });

      await removeDriverWindowSlot({
        id: driverWindowId,
      });

      message.success('Driver window slot and related records deleted successfully');
    } catch (error) {
      const errorMessage = formatError(error, 'Delete Driver Window Slot');
      logError(error, 'Delete Driver Window Slot');
      message.error(`Error deleting driver window slot: ${errorMessage}`);
    }
  };

  const deleteDriverDistrictOld = useDeleteDriverDistrictOld();

  const removeSingleDistrict = useCallback(
    async (districtId: string, slotId: number, dayIndex: number) => {
      const slot = driverWindowSlotsData?.driver_window?.find((s) => s.id === slotId);
      const weekdayWindowId = slot?.weekday_windows.find((ww) => ww.weekday === dayIndex + 1)?.id;
      if (weekdayWindowId) {
        await removeWeekdayWindowDistricts({
          districtId,
          weekdayWindowIds: [weekdayWindowId],
        });
      }
    },
    [driverWindowSlotsData, removeWeekdayWindowDistricts]
  );

  const removeColumnDistrict = useCallback(
    async (districtId: string, dayIndex: number) => {
      const weekdayWindowIds: number[] = [];
      driverWindowSlotsData?.driver_window?.forEach((slot) => {
        const window = slot.weekday_windows.find((ww) => ww.weekday === dayIndex + 1);
        if (window) {
          weekdayWindowIds.push(window.id);
        }
      });
      if (weekdayWindowIds.length > 0) {
        await removeWeekdayWindowDistricts({
          districtId,
          weekdayWindowIds,
        });
      }
    },
    [driverWindowSlotsData, removeWeekdayWindowDistricts]
  );

  const removeWeekDistrict = useCallback(
    async (districtId: string, _slotId: number) => {
      const weekdayWindowsForSlot = await weekdayWindowsForSlotRefetch();
      const weekdayWindowsForSelectedSlot =
        weekdayWindowsForSlot.data as GetWeekdayWindowForSlotQuery;
      const weekdayWindowIds: number[] =
        weekdayWindowsForSelectedSlot?.weekday_window?.map((el) => el.id) ?? [];
      if (weekdayWindowIds.length > 0) {
        await removeWeekdayWindowDistricts({
          districtId,
          weekdayWindowIds,
        });
      }
    },
    [weekdayWindowsForSlotRefetch, removeWeekdayWindowDistricts]
  );

  const removeAllDistricts = useCallback(
    async (districtId: string) => {
      const weekDayWindowsAll = await weekDayWindowsAllRefetch();
      const weekdayWindowsForSelectedSlot = weekDayWindowsAll.data as GetWeekdayWindowForSlotQuery;
      const weekdayWindowIds: number[] =
        weekdayWindowsForSelectedSlot?.weekday_window?.map((el) => el.id) ?? [];
      if (weekdayWindowIds.length > 0) {
        await removeWeekdayWindowDistricts({
          districtId,
          weekdayWindowIds,
        });
      }
    },
    [weekDayWindowsAllRefetch, removeWeekdayWindowDistricts]
  );

  const cleanupAfterDistrictRemove = useCallback(
    async (districtId: string) => {
      const driverId = state.selectedDriverId ?? '';
      const { data } = await weekDayWindowsAllRefetch();
      if (data) {
        const exist = isDistrictPresent(data, districtId);
        if (!exist) {
          await deleteDriverDistrictOld.mutateAsync({ driverId, districtId });
        }
      }
      setLoadingDistrictId(null);
    },
    [
      state.selectedDriverId,
      weekDayWindowsAllRefetch,
      deleteDriverDistrictOld,
      setLoadingDistrictId,
    ]
  );

  const handleRemoveDistrict = async (districtId: string, slotId: number, dayIndex: number) => {
    setLoadingDistrictId(districtId);
    setSelectedSlotId(slotId);
    try {
      if (state.districtDeleteType === DistrictDeleteType.SINGLE) {
        await removeSingleDistrict(districtId, slotId, dayIndex);
      } else if (state.districtDeleteType === DistrictDeleteType.COLUMN) {
        await removeColumnDistrict(districtId, dayIndex);
      } else if (state.districtDeleteType === DistrictDeleteType.WEEK) {
        await removeWeekDistrict(districtId, slotId);
      } else if (state.districtDeleteType === DistrictDeleteType.ALL) {
        await removeAllDistricts(districtId);
      }
    } catch (error) {
      const errorMessage = formatError(error, 'Remove District');
      logError(error, 'Remove District');
      message.error(`Error removing district: ${errorMessage}`);
    } finally {
      await cleanupAfterDistrictRemove(districtId);
    }
  };

  const currentDays = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(startOfWeek(currentWeek, { weekStartsOn: 1 }), index);
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    const dayType = combinedHolidayStyles[formattedDate];
    const dayStyle =
      dayType === 'forced'
        ? 'forced-day'
        : dayType === 'holiday'
          ? 'holiday-day'
          : dayType === 'countryHoliday'
            ? 'country-holiday'
            : '';

    return {
      name: format(date, 'EEEE'),
      date,
      formattedDate: format(date, 'dd MMM'),
      style: dayStyle,
      dayType,
    };
  });

  useEffect(() => {
    if (driverWindowSlotsData) {
      setTimeSlots(
        driverWindowSlotsData.driver_window as {
          id: number;
          timeDuration: string;
          weekday_windows: Weekday_Window[];
        }[]
      );
    }
    // eslint-disable-next-line
  }, [driverWindowSlotsData, setTimeSlots]);

  const handlePreviousWeek = () => {
    setCurrentWeek((prevWeek) => subWeeks(prevWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek((nextWeek) => addWeeks(nextWeek, 1));
  };

  const renderedDays = useMemo(() => {
    return currentDays.map(({ name, formattedDate, style }, _dayIndex) => (
      <div key={`${name}-${formattedDate}`} className={`calendar-day ${style}`}>
        {`${name}, ${formattedDate}`}
      </div>
    ));
  }, [currentDays]);

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <DriverSelect />
        {state.selectedDriverId && (
          <DriverHolidays
            driverId={state.selectedDriverId}
            onHolidaysChange={handleHolidaysChange}
          />
        )}
      </div>
      {driverSlotsLoading && !driverWindowSlotsData ? (
        <CalendarGridSkeleton />
      ) : (
        <Spin spinning={driverSlotsLoading}>
          {!state.selectedDriverId ? (
            <div className="no-driver-selected-warning">
              Please select a driver to view time slots.
            </div>
          ) : (
            <div className="calendar-grid">
              <div className="calendar-day">
                <Button icon={<PlusOutlined />} onClick={handleAddTimeSlotClick} />
              </div>
              {renderedDays}
              {driverWindowSlotsData?.driver_window?.map((slot) => {
                const firstWeekdayWindow = slot.weekday_windows?.[0];
                const { state: slotProviderState, providerNames: slotProviderNames } =
                  getWeekdayWindowProviders(
                    firstWeekdayWindow as WeekdayWindowWithProviders | undefined
                  );
                const slotProviderLabel = getProviderStateLabel(slotProviderState);
                const slotRowClass =
                  slotProviderState === 'express_only'
                    ? 'slot-row-express-only'
                    : slotProviderState === 'both'
                      ? 'slot-row-both'
                      : '';

                return (
                  <React.Fragment key={slot.id}>
                    <div className={`calendar-cell ${slotRowClass}`}>
                      <span className="slot-value" data-time={slot.timeDuration}>
                        {slot.timeDuration}{' '}
                        <EditOutlined
                          onClick={() => handleEditTimeSlot(slot.id, slot.timeDuration ?? '')}
                        />
                      </span>
                      <Tooltip
                        title={`Slot providers: ${slotProviderNames.length > 0 ? slotProviderNames.join(', ') : 'None'}`}
                      >
                        <Tag
                          color={
                            slotProviderState === 'express_only'
                              ? 'orange'
                              : slotProviderState === 'both'
                                ? 'blue'
                                : 'default'
                          }
                          style={{ fontSize: 10, marginTop: 4 }}
                        >
                          {slotProviderLabel}
                        </Tag>
                      </Tooltip>
                      <Popconfirm
                        title="Are you sure you want to remove this time slot?"
                        onConfirm={() => handleRemoveSlot(slot.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="link" danger>
                          Remove
                        </Button>
                      </Popconfirm>
                    </div>

                    {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Complex day cell rendering with provider states */}
                    {currentDays.map(({ date, dayType, name: dayName }, dayIndex) => {
                      const isHolidaySlot = dayType === 'holiday' || dayType === 'countryHoliday';
                      const isForcedSlot = dayType === 'forced';

                      const currentWeekday = slot.weekday_windows.find(
                        (ww) => ww.weekday === dayIndex + 1
                      );

                      const districtsForDay =
                        currentWeekday?.weekday_window_districts
                          .slice()
                          .sort((a, b) =>
                            (a.district?.name ?? '').localeCompare(b.district?.name ?? '')
                          ) || [];

                      const {
                        providerIds,
                        providerNames,
                        state: providerState,
                      } = getWeekdayWindowProviders(
                        currentWeekday as WeekdayWindowWithProviders | undefined
                      );
                      const providerColor = getProviderStateColor(providerState);
                      const providerLabel = getProviderStateLabel(providerState);
                      const hasExpressProvider =
                        providerState === 'express_only' || providerState === 'both';

                      const getProviderCellClass = () => {
                        if (providerState === 'express_only') return 'express-only-cell';
                        if (providerState === 'both') return 'dtk-express-cell';
                        return '';
                      };

                      return (
                        <div
                          key={`${date.getTime()}_${slot.id}`}
                          className={`calendar-cell ${
                            isForcedSlot ? 'forced-slot' : isHolidaySlot ? 'holiday-slot' : ''
                          } ${getProviderCellClass()}`}
                          style={{
                            borderLeft:
                              providerColor !== 'transparent'
                                ? `3px solid ${providerColor}`
                                : undefined,
                          }}
                        >
                          {/* Provider indicator and edit button */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              marginBottom: 4,
                              width: '100%',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Tooltip
                              title={`Providers: ${providerNames.length > 0 ? providerNames.join(', ') : 'None'}`}
                            >
                              <Tag
                                color={
                                  hasExpressProvider
                                    ? providerState === 'both'
                                      ? 'blue'
                                      : 'orange'
                                    : 'default'
                                }
                                style={{ margin: 0, fontSize: 10 }}
                              >
                                {providerLabel}
                              </Tag>
                            </Tooltip>
                            {currentWeekday && (
                              <Tooltip title="Edit providers for this day">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<SettingOutlined />}
                                  onClick={() =>
                                    handleEditWeekdayProviders(
                                      currentWeekday.id,
                                      dayName,
                                      slot.timeDuration ?? '',
                                      providerIds
                                    )
                                  }
                                />
                              </Tooltip>
                            )}
                          </div>

                          <Button
                            icon={<FileAddOutlined />}
                            size={'small'}
                            onClick={() =>
                              handleAddClick(
                                slot.id,
                                dayIndex,
                                districtsForDay
                                  .filter(
                                    (district) => !district.tempDateTo && !district.tempDateFrom
                                  )
                                  .map((district) => district.districtId ?? '') || [],
                                dayjs(date),
                                slot.timeDuration ?? ''
                              )
                            }
                          >
                            Add district
                          </Button>
                          {districtsForDay.map((district) => {
                            const isDisabled = isDistrictDisabledForDay(
                              district.districtId ?? '',
                              dayIndex,
                              disabledWeekdaysForDistricts as Record<string, unknown>
                            );
                            const tooltipMessage = getDistrictTooltipMessage(
                              district.districtId ?? '',
                              disabledWeekdaysForDistricts as Record<string, unknown>
                            );

                            return (
                              <Tooltip
                                key={district.id}
                                title={isDisabled ? tooltipMessage : ''}
                                color={'blue'}
                                placement={'right'}
                              >
                                <div
                                  style={{
                                    alignSelf: 'flex-start',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <DistrictTag
                                    isDisabled={isDisabled}
                                    district={district}
                                    districtDeleteType={state.districtDeleteType}
                                    loadingDistrictId={state.loadingDistrictId}
                                    slot={slot}
                                    dayDate={date}
                                    dayIndex={dayIndex}
                                    handleRemoveDistrict={handleRemoveDistrict}
                                    setBulkDeleteType={setDistrictDeleteType}
                                  />
                                </div>
                              </Tooltip>
                            );
                          })}
                          {districtsForDay.some((district) => {
                            if (district.tempDateFrom && district.tempDateTo) {
                              const tempDateFrom = dayjs(district.tempDateFrom).startOf('day');
                              const tempDateTo = dayjs(district.tempDateTo).endOf('day');
                              const currentDate = dayjs(date).startOf('day');
                              return currentDate.isBetween(tempDateFrom, tempDateTo, null, '[]');
                            }
                            return district;
                          }) && (
                            <Popconfirm
                              title={
                                'Are you sure that you want delete all districts for this day/time?'
                              }
                              onConfirm={async () => {
                                try {
                                  const weekdayWindowId = currentWeekday?.id;
                                  const districtIds = districtsForDay.map(
                                    (district) => district.districtId ?? ''
                                  );

                                  if (weekdayWindowId && districtIds.length > 0) {
                                    await removeWeekdayWindowDistrictsList({
                                      districtIds,
                                      weekdayWindowId,
                                    });
                                    message.success('All districts was deleted');
                                  } else {
                                    message.warning('Something went wrong');
                                  }
                                } catch (error) {
                                  const errorMessage = formatError(error, 'Delete All Districts');
                                  logError(error, 'Delete All Districts');
                                  message.error(`Error deleting districts: ${errorMessage}`);
                                }
                              }}
                            >
                              <Button
                                style={{ alignSelf: 'end', marginTop: 4 }}
                                danger
                                size={'small'}
                                icon={<DeleteFilled />}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {state.selectedDriverId && (
            <div className="calendar-footer">
              <Button
                style={{ marginRight: 4 }}
                icon={<LeftOutlined />}
                onClick={handlePreviousWeek}
              >
                Previous Week
              </Button>
              <Button icon={<RightOutlined />} onClick={handleNextWeek}>
                Next Week
              </Button>
            </div>
          )}

          <SlotModal open={isSlotModalVisible} close={handleCancel} />

          <AddDistrictModal
            departmentId={departmentId}
            open={isDistrictModalVisible}
            close={handleCancel}
            modalTitle={modalTitle || ''}
          />

          <WeekdayProviderModal
            open={isWeekdayProviderModalVisible}
            close={handleCloseWeekdayProviderModal}
            weekdayWindowId={weekdayProviderModalData?.weekdayWindowId ?? null}
            weekdayName={weekdayProviderModalData?.weekdayName ?? ''}
            timeDuration={weekdayProviderModalData?.timeDuration ?? ''}
            currentProviderIds={weekdayProviderModalData?.currentProviderIds ?? []}
          />
        </Spin>
      )}
    </div>
  );
};
